import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'tab',
    'panel',
    'search',
    'sort',
    'embedModal',
    'embedTitle',
    'embedPreview',
    'embedCode',
    'playerModal',
    'playerTitle',
    'playerVideo',
    'playerAudio',
  ];
  static values = { url: String, active: { type: String, default: 'all' } };

  connect() {
    // Tabs we've already fetched. Every tab (including "All") is an
    // infinitely-scrolled server feed.
    this.loaded = new Set();
    // Per-tab infinite-scroll state: { page, hasMore, loading }.
    this.feeds = {};
    this.query = this.hasSearchTarget ? this.searchTarget.value.trim() : '';
    this.sortMode = this.hasSortTarget ? this.sortTarget.value : 'newest';

    this.onClick = this.handleClick.bind(this);
    this.element.addEventListener('click', this.onClick);

    this.createMediaObserver();
    this.createSentinelObserver();

    const panel = this.activePanel();
    if (panel) this.loadTab(this.activeValue, panel);
  }

  disconnect() {
    this.element.removeEventListener('click', this.onClick);
    if (this.mediaObserver) this.mediaObserver.disconnect();
    if (this.sentinelObserver) this.sentinelObserver.disconnect();
    clearTimeout(this._searchTimer);
  }

  switch(event) {
    event.preventDefault();
    const tabName = event.currentTarget.dataset.tab;
    this.activeValue = tabName;

    // Keep the tab in the URL so reloads (e.g. after upload) and the
    // back-from-destroy redirect land on the same tab.
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabName);
    history.replaceState(history.state, '', url);

    this.tabTargets.forEach(t =>
      t.classList.toggle('active', t.dataset.tab === tabName)
    );

    this.panelTargets.forEach(panel => {
      const isTarget = panel.dataset.tab === tabName;
      panel.style.display = isTarget ? 'block' : 'none';
      if (!isTarget) return;

      if (!this.loaded.has(tabName)) this.loadTab(tabName, panel);
    });
  }

  // --- Tab loading ---------------------------------------------------------

  loadTab(tabName, panel) {
    panel.innerHTML =
      '<div class="library-tab-loading"><span class="muted">Loading…</span></div>';
    this.feeds[tabName] = { page: 0, hasMore: true, loading: false };
    this.loaded.add(tabName);
    this.loadFeedPage(tabName, panel, { reset: true });
  }

  loadFeedPage(tabName, panel, { reset = false } = {}) {
    const state = this.feeds[tabName];
    if (!state || state.loading) return;
    if (!reset && !state.hasMore) return;

    state.loading = true;
    const page = reset ? 1 : state.page + 1;

    const params = new URLSearchParams({ tab: tabName, page: String(page) });
    if (this.query) params.set('q', this.query);
    if (this.sortMode) params.set('sort', this.sortMode);

    fetch(`${this.urlValue}?${params.toString()}`, this.fetchOpts())
      .then(r => (r.ok ? r.text() : Promise.reject()))
      .then(html => {
        // Stale-response guard: every loadTab creates a fresh state object, so
        // if ours is no longer the current one, a newer query/sort superseded
        // this request — discard it instead of overwriting newer results.
        if (this.feeds[tabName] !== state) return;

        const tmp = document.createElement('div');
        tmp.innerHTML = html;

        const meta = tmp.querySelector('.library-feed-meta');
        const hasMore = !!meta && meta.dataset.hasMore === 'true';
        const capped = !!meta && meta.dataset.capped === 'true';
        if (meta) meta.remove();

        if (reset) {
          panel.innerHTML = '';
          while (tmp.firstChild) panel.appendChild(tmp.firstChild);
        } else {
          const grid = panel.querySelector('.library-feed-grid');
          if (grid) {
            while (tmp.firstChild) grid.appendChild(tmp.firstChild);
          }
        }

        state.page = page;
        state.hasMore = hasMore;
        state.loading = false;
        state.failures = 0;

        this.observeMedia(panel);
        this.updateSentinel(tabName, panel);
        if (!hasMore && capped) this.showCapNote(panel);
      })
      .catch(() => {
        if (this.feeds[tabName] !== state) return;
        state.loading = false;
        if (reset) {
          panel.innerHTML = this.failHtml();
          // Un-mark the tab so re-clicking it retries the load.
          this.loaded.delete(tabName);
          return;
        }

        // Re-arm the sentinel so a transient error doesn't silently end the
        // scroll. Delayed (observe() fires immediately for an on-screen
        // sentinel) and capped so a persistent failure can't retry forever.
        state.failures = (state.failures || 0) + 1;
        if (state.failures < 3) {
          setTimeout(() => this.updateSentinel(tabName, panel), 2000);
        }
      });
  }

  // Shown once when we stop at the MAX_SCAN ceiling (studios with thousands of
  // items in a tab). Placed after the grid, not inside it.
  showCapNote(panel) {
    if (panel.querySelector('.library-feed-note')) return;
    const note = document.createElement('p');
    note.className = 'library-feed-note muted';
    note.textContent =
      'Showing your most recent 2,000 files. Older files remain accessible from their original event, user, or document pages.';
    panel.appendChild(note);
  }

  // Re-arm the sentinel after each page so a tall viewport keeps filling until
  // the sentinel scrolls out of view or there's nothing left to load.
  updateSentinel(tabName, panel) {
    const sentinel = panel.querySelector('.library-feed-sentinel');
    if (!sentinel) return;

    this.sentinelObserver.unobserve(sentinel);
    const state = this.feeds[tabName];
    if (state && state.hasMore) {
      sentinel.dataset.tab = tabName;
      sentinel.style.display = '';
      this.sentinelObserver.observe(sentinel);
    } else {
      sentinel.style.display = 'none';
    }
  }

  createSentinelObserver() {
    this.sentinelObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const tabName = entry.target.dataset.tab;
          const panel = this.panelTargets.find(p => p.dataset.tab === tabName);
          if (panel) this.loadFeedPage(tabName, panel);
        });
      },
      { rootMargin: '400px' }
    );
  }

  fetchOpts() {
    return {
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
    };
  }

  failHtml() {
    return '<p class="muted center">Failed to load. Please try again.</p>';
  }

  // --- Search + sort -------------------------------------------------------

  filter() {
    this.query = this.hasSearchTarget ? this.searchTarget.value.trim() : '';
    clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => this.onSearchOrSortChanged(), 250);
  }

  resort() {
    // Cancel any pending debounced search so it can't fire a second, stale
    // reload right after this one.
    clearTimeout(this._searchTimer);
    this.sortMode = this.hasSortTarget ? this.sortTarget.value : 'newest';
    this.onSearchOrSortChanged();
  }

  onSearchOrSortChanged() {
    const tab = this.activeValue;
    const panel = this.activePanel();
    if (!panel) return;

    // All tabs filter/sort server-side. The active tab reloads now; other loaded
    // tabs are marked stale so they refetch with the new query/sort when opened.
    this.loaded.clear();
    this.loadTab(tab, panel);
  }

  activePanel() {
    return this.panelTargets.find(
      panel => panel.dataset.tab === this.activeValue
    );
  }

  // --- Delegated actions (rename + empty-state upload + embed) -------------

  handleClick(event) {
    // Recordings play in the modal instead of navigating to the (S3) file
    // URL, which many browsers treat as a download.
    const playable = event.target.closest('.library-card-playable');
    if (playable && this.element.contains(playable)) {
      event.preventDefault();
      this.openPlayer(playable);
      return;
    }

    const renameButton = event.target.closest('.library-card-rename');
    if (renameButton && this.element.contains(renameButton)) {
      event.preventDefault();
      this.startRename(renameButton);
      return;
    }

    const uploadButton = event.target.closest('.library-empty-upload');
    if (uploadButton && this.element.contains(uploadButton)) {
      event.preventDefault();
      const fileInput = document.querySelector(
        '[data-library-upload-target="fileInput"]'
      );
      if (fileInput) fileInput.click();
      return;
    }

    const embedButton = event.target.closest('.library-card-embed');
    if (embedButton && this.element.contains(embedButton)) {
      event.preventDefault();
      this.openEmbed(embedButton);
      return;
    }

    const copyLinkButton = event.target.closest('.library-card-copylink');
    if (copyLinkButton && this.element.contains(copyLinkButton)) {
      event.preventDefault();
      this.copyLink(copyLinkButton);
      return;
    }

    const visibilityButton = event.target.closest('.library-card-visibility');
    if (visibilityButton && this.element.contains(visibilityButton)) {
      event.preventDefault();
      this.togglePublic(visibilityButton);
      return;
    }

    const onDemandButton = event.target.closest('.library-card-ondemand');
    if (onDemandButton && this.element.contains(onDemandButton)) {
      event.preventDefault();
      this.toggleOnDemand(onDemandButton);
      return;
    }

    const featureButton = event.target.closest('.library-card-feature');
    if (featureButton && this.element.contains(featureButton)) {
      event.preventDefault();
      this.toggleFeatured(featureButton);
    }
  }

  // --- Public / featured toggles -------------------------------------------

  togglePublic(button) {
    if (button.disabled) return;
    const makePublic = button.dataset.public !== 'true';
    button.disabled = true;

    this.patchJson(button.dataset.visibilityUrl, { public: makePublic })
      .then(state => {
        const card = button.closest('.library-card');
        button.dataset.public = String(state.public);
        button.classList.toggle('is-public', state.public);
        button.classList.toggle('is-private', !state.public);
        // The not-public wording differs by asset type ("Students only" on an
        // event recording, "Private" on an upload), so the server stamps it on
        // the button and this handler just reads it back — one source of truth.
        this.setChipLabel(
          button,
          state.public ? 'Public' : (button.dataset.unpublishedLabel || 'Private')
        );
        button.title = state.public
          ? 'Public — click to make private'
          : (button.dataset.unpublishedTitle || 'Private — click to make public');

        // Embed, copy-link + feature are only available on public recordings.
        const feature = card && card.querySelector('.library-card-feature');
        const embed = card && card.querySelector('.library-card-embed');
        const copyLink = card && card.querySelector('.library-card-copylink');
        [feature, embed, copyLink].forEach(el => {
          if (el) el.style.display = state.public ? '' : 'none';
        });
        if (feature) this.setFeatureButton(feature, state.featured);

        // The visibility ladder also moves the On-Demand flag server-side
        // (public adds it, private removes it) — reflect that here.
        const onDemand = card && card.querySelector('.library-card-ondemand');
        if (onDemand && typeof state.on_demand !== 'undefined') {
          this.setOnDemandButton(onDemand, state.on_demand);
        }

        this.showToast(
          state.public ? 'Video is now public.' : 'Video is now private.'
        );
      })
      .catch(() => this.showToast('Could not update. Please try again.', true))
      .finally(() => {
        button.disabled = false;
      });
  }

  toggleFeatured(button) {
    if (button.disabled) return;
    const makeFeatured = button.dataset.featured !== 'true';
    button.disabled = true;

    this.patchJson(button.dataset.featureUrl, { featured: makeFeatured })
      .then(state => {
        // There's one featured recording studio-wide, so clear every other
        // loaded feature button before marking this one.
        if (state.featured) {
          this.element.querySelectorAll('.library-card-feature').forEach(el => {
            if (el !== button) this.setFeatureButton(el, false);
          });
        }
        this.setFeatureButton(button, state.featured);
        this.showToast(
          state.featured ? 'Set as featured video.' : 'Removed from featured.'
        );
      })
      .catch(() => this.showToast('Could not update. Please try again.', true))
      .finally(() => {
        button.disabled = false;
      });
  }

  toggleOnDemand(button) {
    if (button.disabled) return;
    const makeAvailable = button.dataset.onDemand !== 'true';
    button.disabled = true;

    this.patchJson(button.dataset.ondemandUrl, { on_demand: makeAvailable })
      .then(state => {
        this.setOnDemandButton(button, state.on_demand);
        this.showToast(
          state.on_demand
            ? 'Added to the student On-Demand library.'
            : 'Removed from the student On-Demand library.'
        );
      })
      .catch(() => this.showToast('Could not update. Please try again.', true))
      .finally(() => {
        button.disabled = false;
      });
  }

  setOnDemandButton(button, available) {
    button.dataset.onDemand = String(available);
    button.classList.toggle('is-on', available);
    button.title = available
      ? 'In the student On-Demand library — click to remove'
      : 'Show in the student On-Demand library';
  }

  setFeatureButton(button, featured) {
    button.dataset.featured = String(featured);
    button.classList.toggle('is-featured', featured);
    button.title = featured
      ? 'Featured video — click to remove'
      : "Set as the studio's featured video";
    this.setChipLabel(button, featured ? 'Featured' : 'Feature');
  }

  setChipLabel(button, text) {
    const label = button.querySelector('.lib-chip-label');
    if (label) label.textContent = text;
  }

  patchJson(url, body) {
    return fetch(url, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': this.csrfToken(),
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(response => {
      if (!response.ok) throw new Error('Request failed');
      return response.json();
    });
  }

  // --- Video embed modal ---------------------------------------------------

  openEmbed(button) {
    if (!this.hasEmbedModalTarget) return;

    const url = button.dataset.embedUrl;
    if (!url) return;
    const title = button.dataset.embedTitle || 'this video';

    // Built here (not server-side) so the snippet always matches the URL we
    // also feed the live preview iframe below.
    const code = `<iframe src="${url}" width="640" height="360" style="border:0; max-width:100%;" allow="fullscreen; picture-in-picture" allowfullscreen></iframe>`;

    this.embedCodeTarget.value = code;
    if (this.hasEmbedTitleTarget) this.embedTitleTarget.textContent = title;
    if (this.hasEmbedPreviewTarget) this.embedPreviewTarget.src = url;
    this.embedModalTarget.style.display = 'flex';
  }

  closeEmbed(event) {
    if (event) event.preventDefault();
    if (!this.hasEmbedModalTarget) return;
    this.embedModalTarget.style.display = 'none';
    // Stop playback / release the connection by clearing the preview.
    if (this.hasEmbedPreviewTarget) this.embedPreviewTarget.src = 'about:blank';
  }

  // Only close when the backdrop itself (not the dialog) is clicked.
  closeEmbedBackdrop(event) {
    if (event.target === this.embedModalTarget) this.closeEmbed(event);
  }

  // --- In-page recording player ---------------------------------------------

  openPlayer(el) {
    if (!this.hasPlayerModalTarget) return;

    const url = el.dataset.playerUrl;
    if (!url) return;
    const isVideo = el.dataset.playerKind === 'video';

    if (this.hasPlayerTitleTarget) {
      this.playerTitleTarget.textContent =
        el.dataset.playerTitle || 'Recording';
    }

    const media = isVideo ? this.playerVideoTarget : this.playerAudioTarget;
    const other = isVideo ? this.playerAudioTarget : this.playerVideoTarget;

    this.stopPlayerMedia(other);
    other.style.display = 'none';
    media.style.display = '';
    media.src = url;
    this.playerModalTarget.style.display = 'flex';
    media.play().catch(() => {}); // autoplay may be blocked; controls remain
  }

  closePlayer(event) {
    if (event) event.preventDefault();
    if (!this.hasPlayerModalTarget) return;
    this.stopPlayerMedia(this.playerVideoTarget);
    this.stopPlayerMedia(this.playerAudioTarget);
    this.playerModalTarget.style.display = 'none';
  }

  closePlayerBackdrop(event) {
    if (event.target === this.playerModalTarget) this.closePlayer(event);
  }

  stopPlayerMedia(media) {
    if (!media) return;
    media.pause();
    media.removeAttribute('src');
    media.load();
  }

  // Copies the video's direct (embed-page) URL — a shareable public link.
  copyLink(button) {
    const url = button.dataset.linkUrl;
    if (!url) return;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => this.showToast('Link copied.'))
        .catch(() => this.showToast('Could not copy the link.', true));
      return;
    }

    // Fallback for older browsers: select the text in a temporary input.
    const input = document.createElement('textarea');
    input.value = url;
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.focus();
    input.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (e) {
      ok = false;
    }
    input.remove();
    this.showToast(ok ? 'Link copied.' : 'Could not copy the link.', !ok);
  }

  copyEmbed(event) {
    if (event) event.preventDefault();
    if (!this.hasEmbedCodeTarget) return;

    const code = this.embedCodeTarget.value;
    this.embedCodeTarget.focus();
    this.embedCodeTarget.select();

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(code)
        .then(() => this.showToast('Embed code copied.'))
        .catch(() => this.showToast('Press Ctrl/Cmd+C to copy.', true));
      return;
    }

    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (e) {
      ok = false;
    }
    this.showToast(
      ok ? 'Embed code copied.' : 'Press Ctrl/Cmd+C to copy.',
      !ok
    );
  }

  startRename(button) {
    const nameEl = button.closest('.library-card-name');
    if (!nameEl || nameEl.querySelector('.library-card-rename-input')) return;

    const link = nameEl.querySelector('.library-card-name-link');
    if (!link) return;

    const originalName = link.textContent.trim();

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'library-card-rename-input';
    input.value = originalName;
    input.maxLength = 255;

    link.style.display = 'none';
    button.style.display = 'none';
    nameEl.appendChild(input);
    input.focus();
    input.select();

    let finished = false;

    const cleanup = () => {
      finished = true;
      input.remove();
      link.style.display = '';
      button.style.display = '';
    };

    const commit = () => {
      if (finished || input.disabled) return;

      const newName = input.value.trim();
      if (!newName || newName === originalName) {
        cleanup();
        return;
      }

      input.disabled = true;
      input.classList.remove('error');

      fetch(button.dataset.renameUrl, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': this.csrfToken(),
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ name: newName }),
      })
        .then(response => {
          if (!response.ok) throw new Error('Rename failed');
          return response.json();
        })
        .then(data => {
          cleanup();
          this.updateCardName(button, data.name || newName);
          this.showToast(`Renamed to '${data.name || newName}'.`);
        })
        .catch(() => {
          input.disabled = false;
          input.classList.add('error');
          input.focus();
          this.showToast('Could not rename. Please try again.', true);
        });
    };

    input.addEventListener('keydown', event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commit();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        cleanup();
      }
    });

    input.addEventListener('blur', () => {
      // Allow the error state to keep the input around for another try.
      if (!finished && !input.disabled && !input.classList.contains('error')) {
        commit();
      } else if (input.classList.contains('error')) {
        cleanup();
      }
    });
  }

  // The same asset can appear in multiple loaded panels ("All" + its type
  // tab), so sync the new name everywhere.
  updateCardName(button, name) {
    const card = button.closest('.library-card');
    const assetId = card && card.dataset.libraryAssetId;
    const cards = assetId
      ? this.element.querySelectorAll(`[data-library-asset-id="${assetId}"]`)
      : [card].filter(Boolean);

    cards.forEach(instance => {
      instance.dataset.name = name.toLowerCase();
      const nameEl = instance.querySelector('.library-card-name');
      if (!nameEl) return;
      nameEl.title = name;
      const link = nameEl.querySelector('.library-card-name-link');
      if (link) link.textContent = name;
    });
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : '';
  }

  showToast(message, isError) {
    const toast = document.createElement('div');
    toast.className = `library-toast${isError ? ' error' : ''}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('visible'));
    setTimeout(() => {
      toast.classList.remove('visible');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  // --- Lazy media loading --------------------------------------------------

  // IntersectionObserver for lazy loading media (images + videos)
  observeMedia(root) {
    const container = root || this.element;

    container
      .querySelectorAll(
        'img[data-lazy-src], video[data-lazy-src], object[data-lazy-data]'
      )
      .forEach(el => {
        if (!this.mediaObserver) this.createMediaObserver();
        this.mediaObserver.observe(el);
      });
  }

  createMediaObserver() {
    this.mediaObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const el = entry.target;

            if (el.tagName === 'IMG') {
              el.src = el.dataset.lazySrc;
              delete el.dataset.lazySrc;
            } else if (el.tagName === 'VIDEO') {
              const source = el.querySelector('source');
              if (source) {
                source.src = el.dataset.lazySrc;
                el.load();
              }
              delete el.dataset.lazySrc;
            } else if (el.tagName === 'OBJECT') {
              el.data = el.dataset.lazyData;
              delete el.dataset.lazyData;
            }

            this.mediaObserver.unobserve(el);
          }
        });
      },
      {
        rootMargin: '200px', // Start loading 200px before visible
      }
    );
  }
}
